import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { db } from '../db/db';
import { normalizeForSearch } from '../db/normalize';
import { SearchBar } from '../components/SearchBar';
import { ProgressBar } from '../components/ProgressBar';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../auth/AuthContext';
import { useJobStatusSync } from '../hooks/useJobStatusSync';

type Filter = 'all' | 'not_photographed' | 'photographed' | 'spare';

export function JobOverviewPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [menuOpen, setMenuOpen] = useState(false);

  useJobStatusSync(jobId);

  const job = useLiveQuery(() => (jobId ? db.jobs.get(jobId) : undefined), [jobId]);
  const groups = useLiveQuery(() => (jobId ? db.groups.where('jobId').equals(jobId).sortBy('order') : []), [jobId]);
  const subjects = useLiveQuery(() => (jobId ? db.subjects.where('jobId').equals(jobId).toArray() : []), [jobId]);

  const normalizedQuery = normalizeForSearch(query);
  const searchResults = useMemo(() => {
    if (!normalizedQuery || !subjects) return [];
    return subjects.filter(s => s.searchNormalized.includes(normalizedQuery)).slice(0, 50);
  }, [normalizedQuery, subjects]);

  if (!job || !groups || !subjects) return <div className="screen screen--centered">Loading…</div>;

  const groupStats = groups.map(g => {
    const members = subjects.filter(s => s.groupId === g.id && !s.excluded);
    const completed = members.filter(s => s.status === 'photographed').length;
    return { group: g, total: members.length, completed };
  });

  const lastViewedKey = `qr-cards-last-viewed-${jobId}`;
  function handleContinue() {
    const lastSubjectId = sessionStorage.getItem(lastViewedKey);
    const target = lastSubjectId && subjects!.some(s => s.id === lastSubjectId)
      ? lastSubjectId
      : subjects!.find(s => s.status === 'not_photographed' && !s.excluded)?.id;
    if (target) navigate(`/jobs/${jobId}/subjects/${target}`);
  }

  return (
    <div className="screen">
      <header className="app-header">
        <div>
          <Link to="/jobs" className="back-link">
            ← Jobs
          </Link>
          <h1>{job.name}</h1>
          <p className="app-header-sub">{job.schoolLabel}</p>
        </div>
        <div className="app-header-actions">
          <button className="btn btn--icon" onClick={() => setMenuOpen(v => !v)} aria-label="Job menu">
            ⋮
          </button>
          {menuOpen && (
            <div className="overflow-menu">
              <button onClick={() => navigate(`/jobs/${jobId}/info`)}>Job info</button>
              <button onClick={() => navigate('/import')}>Re-import / update</button>
              {session?.role === 'admin' && (
                <button onClick={() => navigate(`/jobs/${jobId}/assign`)}>Assign photographers</button>
              )}
            </div>
          )}
        </div>
      </header>

      <SearchBar value={query} onChange={setQuery} autoFocus />

      {normalizedQuery ? (
        <ul className="subject-list">
          {searchResults.map(s => (
            <li key={s.id}>
              <Link to={`/jobs/${jobId}/subjects/${s.id}`} className="subject-row">
                <span className="subject-row-name">{s.fullName}</span>
                <span className="subject-row-meta">{groups.find(g => g.id === s.groupId)?.name}</span>
                <StatusBadge status={s.excluded ? 'excluded' : s.status} warning={s.importWarnings.some(w => w.severity !== 'info')} />
              </Link>
            </li>
          ))}
          {searchResults.length === 0 && <p className="empty-state-inline">No matches.</p>}
        </ul>
      ) : (
        <>
          <button className="btn btn--primary btn--large continue-btn" onClick={handleContinue}>
            Continue
          </button>

          <div className="filter-row">
            {(['all', 'not_photographed', 'photographed', 'spare'] as Filter[]).map(f => (
              <button key={f} className={filter === f ? 'filter-chip filter-chip--active' : 'filter-chip'} onClick={() => setFilter(f)}>
                {filterLabel(f)}
              </button>
            ))}
          </div>

          <ul className="group-list">
            {groupStats
              .filter(({ group, completed, total }) => matchesFilter(filter, group.name, completed, total))
              .map(({ group, completed, total }) => (
                <li key={group.id}>
                  <Link to={`/jobs/${jobId}/groups/${group.id}`} className="group-card">
                    <span className="group-card-name">{group.name}</span>
                    <ProgressBar completed={completed} total={total} />
                  </Link>
                </li>
              ))}
          </ul>
        </>
      )}
    </div>
  );
}

function filterLabel(f: Filter): string {
  switch (f) {
    case 'all':
      return 'All';
    case 'not_photographed':
      return 'Not photographed';
    case 'photographed':
      return 'Completed';
    case 'spare':
      return 'Spare';
  }
}

function matchesFilter(filter: Filter, groupName: string, completed: number, total: number): boolean {
  if (filter === 'all') return true;
  if (filter === 'spare') return /spare/i.test(groupName);
  if (filter === 'photographed') return completed > 0;
  if (filter === 'not_photographed') return completed < total;
  return true;
}
